import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Paper, Table, TableHead, TableBody,
    TableRow, TableCell, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
    Alert, InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/api';

const roleLabels = { admin: 'Directeur', director: 'RH', trainer: 'Formateur' };
const roleColors = { admin: 'secondary', director: 'primary', trainer: 'success' };

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [istas, setIstas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [dialog, setDialog] = useState(false);
    const [form, setForm] = useState({
        nom: '', prenom: '', email: '', password: '', password_confirmation: '',
        role: 'trainer', phone: '', cin: '', diplomes: '', profession: '', ista_id: '',
    });
    const [error, setError] = useState('');

    useEffect(() => { loadData(); }, []);

    const loadData = async () => {
        try {
            const [usersRes, istasRes] = await Promise.allSettled([
                adminAPI.getUsers(),
                adminAPI.getIstas(),
            ]);
            if (usersRes.status === 'fulfilled') setUsers(usersRes.value.data);
            if (istasRes.status === 'fulfilled') setIstas(istasRes.value.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleCreate = async () => {
        setError('');
        try {
            const payload = { ...form };
            // Only send ista_id for RH (director) users
            if (payload.role !== 'director') {
                delete payload.ista_id;
            }
            await adminAPI.createUser(payload);
            toast.success('Compte créé');
            setDialog(false);
            setForm({ nom: '', prenom: '', email: '', password: '', password_confirmation: '', role: 'trainer', phone: '', cin: '', diplomes: '', profession: '', ista_id: '' });
            loadData();
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.errors;
            setError(typeof msg === 'object' ? Object.values(msg).flat().join('. ') : msg || 'Erreur');
        }
    };

    const handleToggle = async (id) => {
        try {
            await adminAPI.toggleUser(id);
            toast.success('Statut modifié');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur');
        }
    };

    const handleAssignIsta = async (userId, istaId) => {
        try {
            await adminAPI.assignIsta(userId, { ista_id: istaId || null });
            toast.success('ISTA assigné');
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.message || 'Erreur');
        }
    };

    const filtered = users.filter(u =>
        !search ||
        (u.nom && u.nom.toLowerCase().includes(search.toLowerCase())) ||
        (u.prenom && u.prenom.toLowerCase().includes(search.toLowerCase())) ||
        u.email.toLowerCase().includes(search.toLowerCase())
    );

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="h5" fontWeight={700}>Gérer les comptes</Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        size="small" placeholder="Rechercher..."
                        value={search} onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                        }}
                    />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialog(true)}
                        sx={{ background: 'linear-gradient(135deg, #1a237e, #283593)', textTransform: 'none', borderRadius: 2 }}>
                        Ajouter compte
                    </Button>
                </Box>
            </Box>

            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
                <div className="responsive-table-wrapper">
                    <Table>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                                <TableCell sx={{ fontWeight: 600 }}>Nom</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Prénom</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Rôle</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>ISTA</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Statut</TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.map(u => (
                                <TableRow key={u.id} hover>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon color="action" fontSize="small" />
                                            <Typography variant="body2" fontWeight={600}>{u.nom}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell>{u.prenom || '—'}</TableCell>
                                    <TableCell>{u.email}</TableCell>
                                    <TableCell>
                                        <Chip label={roleLabels[u.role] || u.role} color={roleColors[u.role] || 'default'} size="small" />
                                    </TableCell>
                                    <TableCell>
                                        {u.role === 'director' ? (
                                            <TextField
                                                select size="small" variant="standard"
                                                value={u.ista_id || ''}
                                                onChange={(e) => handleAssignIsta(u.id, e.target.value)}
                                                sx={{ minWidth: 130 }}
                                            >
                                                <MenuItem value="">— Aucun —</MenuItem>
                                                {istas.map(i => (
                                                    <MenuItem key={i.id} value={i.id}>{i.nom} ({i.code})</MenuItem>
                                                ))}
                                            </TextField>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">—</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={u.is_active ? 'Actif' : 'Inactif'}
                                            color={u.is_active ? 'success' : 'default'}
                                            size="small" variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            size="small" variant="outlined"
                                            color={u.is_active ? 'error' : 'success'}
                                            onClick={() => handleToggle(u.id)}
                                            sx={{ textTransform: 'none' }}
                                        >
                                            {u.is_active ? 'Désactiver' : 'Activer'}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </Paper>

            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nouveau compte</DialogTitle>
                <DialogContent>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    <Box sx={{ display: 'flex', gap: 2, mt: 1, mb: 2 }}>
                        <TextField fullWidth label="Nom" value={form.nom} onChange={(e) => setForm({ ...form, nom: e.target.value })} />
                        <TextField fullWidth label="Prénom" value={form.prenom} onChange={(e) => setForm({ ...form, prenom: e.target.value })} />
                    </Box>
                    <TextField fullWidth label="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} sx={{ mb: 2 }} />
                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField fullWidth label="Téléphone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                        <TextField fullWidth label="CIN" value={form.cin} onChange={(e) => setForm({ ...form, cin: e.target.value })} />
                    </Box>
                    <TextField select fullWidth label="Rôle" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value, ista_id: '' })} sx={{ mb: 2 }}>
                        <MenuItem value="admin">Directeur</MenuItem>
                        <MenuItem value="director">RH</MenuItem>
                        <MenuItem value="trainer">Formateur</MenuItem>
                    </TextField>

                    {/* Show ISTA select only for RH role */}
                    {form.role === 'director' && (
                        <TextField select fullWidth label="ISTA assigné" value={form.ista_id} onChange={(e) => setForm({ ...form, ista_id: e.target.value })} sx={{ mb: 2 }}>
                            <MenuItem value="">— Aucun —</MenuItem>
                            {istas.map(i => (
                                <MenuItem key={i.id} value={i.id}>{i.nom} ({i.code})</MenuItem>
                            ))}
                        </TextField>
                    )}

                    <TextField fullWidth label="Profession" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} sx={{ mb: 2 }} />
                    <TextField fullWidth label="Diplômes" value={form.diplomes} onChange={(e) => setForm({ ...form, diplomes: e.target.value })} multiline rows={2} sx={{ mb: 2 }} />
                    <TextField fullWidth label="Mot de passe" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} sx={{ mb: 2 }} />
                    <TextField fullWidth label="Confirmer" type="password" value={form.password_confirmation} onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)} sx={{ textTransform: 'none' }}>Annuler</Button>
                    <Button variant="contained" onClick={handleCreate} sx={{ textTransform: 'none', background: 'linear-gradient(135deg, #1a237e, #283593)' }}>
                        Créer
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
